import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';

import { environment } from '../../../environments/environment';
import {
  CorrectAccountPayload,
  Declaration,
  DeclarationsResponse,
  DeclarePayload,
} from './declaration.types';

/**
 * The declared-lists register (FLOW-243) — accounts, funders, related parties,
 * directors, and the affirmation that the account list is complete.
 *
 * **Two doors onto one register**, and which one a caller uses is not a
 * convenience: the client door records `CLIENT_DECLARED` (the business's own
 * statement about itself), the Ops door records `OPS_ADDED` (what a statement
 * revealed). A client screen must never call the Ops methods.
 *
 * 🚨 **There is no delete.** A correction is a withdrawal, which appends a new
 * row naming the one it retracts — the corrected row stays readable and stays
 * attributable to whoever made it.
 */
@Injectable({ providedIn: 'root' })
export class DeclarationService {
  private readonly http = inject(HttpClient);

  private base(entityShortId: string): string {
    return `${environment.apiUrl}/api/entities/${entityShortId}/declarations/`;
  }

  private opsBase(entityShortId: string): string {
    return `${environment.apiUrl}/api/entities/ops/${entityShortId}/declarations/`;
  }

  /** The business's own declarations — its members and its active agents. */
  list(entityShortId: string): Observable<DeclarationsResponse> {
    return this.http.get<DeclarationsResponse>(this.base(entityShortId));
  }

  declare(entityShortId: string, payload: DeclarePayload): Observable<Declaration> {
    return this.http.post<Declaration>(this.base(entityShortId), payload);
  }

  /** Append the WITHDRAWN row that retracts one declaration. Not a delete. */
  withdraw(entityShortId: string, declShortId: string): Observable<Declaration> {
    return this.http.post<Declaration>(`${this.base(entityShortId)}${declShortId}/withdraw/`, {});
  }

  /**
   * Correct an account the system read off a statement (FC-DET-047). The
   * derived row is withdrawn and a corrected row appended with `corrects` set;
   * the statements follow the account. Also the door for "whose account is
   * this?" — sent without the identity, the number stands.
   */
  correct(
    entityShortId: string,
    declShortId: string,
    payload: CorrectAccountPayload,
  ): Observable<Declaration> {
    return this.http.post<Declaration>(
      `${this.base(entityShortId)}${declShortId}/correct/`,
      payload,
    );
  }

  /** Ops read — the whole row, including the two fields N4 keeps from clients. */
  opsList(entityShortId: string): Observable<DeclarationsResponse> {
    return this.http.get<DeclarationsResponse>(this.opsBase(entityShortId));
  }

  opsDeclare(entityShortId: string, payload: DeclarePayload): Observable<Declaration> {
    return this.http.post<Declaration>(this.opsBase(entityShortId), payload);
  }

  opsWithdraw(entityShortId: string, declShortId: string): Observable<Declaration> {
    return this.http.post<Declaration>(
      `${this.opsBase(entityShortId)}${declShortId}/withdraw/`,
      {},
    );
  }
}
